import type { MenuItem } from "./MenuItem";

// Keyboard navigation of a menu with sub-menus. What is selected is a path:
// the index of the selected item in the top menu, then in the sub-menu open
// under it, and so on. An empty path means nothing is selected.
export type MenuPath = number[];

export type MenuKeyResult = {
  // false if the key means nothing to a menu
  handled: boolean;
  path: MenuPath;
  // the menu should be closed
  close?: boolean;
  // the item to run, after which the menu should be closed
  run?: MenuItem;
};

// Returns the items of the level the last index of the path is in.
function itemsOfLastLevel(menu: MenuItem[], path: MenuPath): MenuItem[] {
  let items = menu;
  for (let i = 0; i < path.length - 1; i++) {
    items = items[path[i]]?.items ?? [];
  }
  return items;
}

// Returns the index of the next item that can be selected, going around at
// the ends and skipping separators. -1 if there is none.
function step(items: MenuItem[], from: number, dir: number): number {
  let n = items.length;
  let idx = from;
  if (idx < 0) {
    idx = dir > 0 ? -1 : n;
  }
  for (let i = 0; i < n; i++) {
    idx = (idx + dir + n) % n;
    if (!items[idx].isSeparator()) {
      return idx;
    }
  }
  return -1;
}

// Returns the path that selects the first item of a menu.
export function menuFirst(menu: MenuItem[]): MenuPath {
  let idx = step(menu, -1, 1);
  return idx < 0 ? [] : [idx];
}

// Returns the selected item, or null.
export function menuSelected(menu: MenuItem[], path: MenuPath): MenuItem | null {
  if (path.length == 0) {
    return null;
  }
  return itemsOfLastLevel(menu, path)[path[path.length - 1]] ?? null;
}

// Returns what a key does in a menu with the given selection.
//   Up, Down: move in the current menu
//   Right, Enter: open the sub-menu of the selected item, select its first item
//   Left: close the sub-menu and go back to its parent; closes the top menu
//   Enter: run the selected item
//   Escape: close the menu
export function menuKey(menu: MenuItem[], path: MenuPath, key: string): MenuKeyResult {
  let res: MenuKeyResult = { handled: true, path: path };
  if (key == "Escape") {
    res.close = true;
    return res;
  }
  if (key == "ArrowDown" || key == "ArrowUp") {
    let dir = key == "ArrowDown" ? 1 : -1;
    if (path.length == 0) {
      let idx = step(menu, -1, dir);
      res.path = idx < 0 ? [] : [idx];
      return res;
    }
    let idx = step(itemsOfLastLevel(menu, path), path[path.length - 1], dir);
    if (idx >= 0) {
      res.path = [...path.slice(0, -1), idx];
    }
    return res;
  }
  if (key == "ArrowLeft") {
    if (path.length > 1) {
      res.path = path.slice(0, -1);
    } else {
      res.close = true;
    }
    return res;
  }
  if (key == "ArrowRight" || key == "Enter") {
    let item = menuSelected(menu, path);
    if (!item) {
      return res;
    }
    if (item.isSubMenu()) {
      let idx = step(item.items, -1, 1);
      if (idx >= 0) {
        res.path = [...path, idx];
      }
      return res;
    }
    if (key == "Enter") {
      res.run = item;
    }
    return res;
  }
  res.handled = false;
  return res;
}
